/** Certified-mail / parcel tracking deep links by debtor country. */

import { coerceDebtorCountry, normalizeDebtorCountry } from './debtorCountry.js';
import { normalizePostalCode } from './postalCode.js';

const CA_PROVINCES = new Set([
  'AB',
  'BC',
  'MB',
  'NB',
  'NL',
  'NS',
  'NT',
  'NU',
  'ON',
  'PE',
  'QC',
  'SK',
  'YT',
]);

const US_ZIP = /^\d{5}(?:-?\d{4})?$/;
const CA_POSTAL = /^[ABCEGHJ-NPRSTVXY]\d[ABCEGHJ-NPRSTV-Z]\d[ABCEGHJ-NPRSTV-Z]\d$/i;

/** Strip spaces/dashes so carrier lookup gets a clean label. */
export function normalizeTrackingNumber(value) {
  return String(value || '')
    .replace(/[\s-]+/g, '')
    .trim();
}

export function hasTrackingNumber(value) {
  return normalizeTrackingNumber(value).length >= 8;
}

/**
 * Infer destination country for mail tracking.
 * Prefers explicit Debtor_Country, then postal / province, then tracking heuristics.
 */
export function inferMailCountry({ country, postalCode, state, trackingNumber } = {}) {
  const explicit = coerceDebtorCountry(country);
  if (explicit) return explicit;

  const postal = normalizePostalCode(postalCode || '');
  const compactPostal = postal.replace(/\s+/g, '');
  if (compactPostal && CA_POSTAL.test(compactPostal)) return 'CA';
  if (postal && (US_ZIP.test(postal) || US_ZIP.test(compactPostal))) return 'US';

  const region = String(state || '')
    .trim()
    .toUpperCase();
  if (region && CA_PROVINCES.has(region)) return 'CA';

  const label = normalizeTrackingNumber(trackingNumber);
  if (/^[A-Z0-9]{2}\d{9}CA$/i.test(label)) return 'CA';
  if (/^[A-Z0-9]{2}\d{9}GB$/i.test(label)) return 'GB';
  if (/^[A-Z0-9]{2}\d{9}AU$/i.test(label)) return 'AU';
  if (/^[A-Z0-9]{2}\d{9}NZ$/i.test(label)) return 'NZ';
  if (/^[A-Z0-9]{2}\d{9}IE$/i.test(label)) return 'IE';
  if (/^9\d{19,21}$/.test(label)) return 'US';

  return normalizeDebtorCountry(country, { postalCode, state });
}

export function uspsTrackingUrl(value) {
  const label = normalizeTrackingNumber(value);
  if (!label) return null;
  return `https://tools.usps.com/go/TrackConfirmAction?tLabels=${encodeURIComponent(label)}`;
}

export function canadaPostTrackingUrl(value) {
  const label = normalizeTrackingNumber(value);
  if (!label) return null;
  return `https://www.canadapost-postescanada.ca/track-reperage/en#/details/${encodeURIComponent(label)}`;
}

export function royalMailTrackingUrl(value) {
  const label = normalizeTrackingNumber(value);
  if (!label) return null;
  return `https://www.royalmail.com/track-your-item#/tracking-results/${encodeURIComponent(label)}`;
}

export function australiaPostTrackingUrl(value) {
  const label = normalizeTrackingNumber(value);
  if (!label) return null;
  return `https://auspost.com.au/mypost/track/details/${encodeURIComponent(label)}`;
}

export function nzPostTrackingUrl(value) {
  const label = normalizeTrackingNumber(value);
  if (!label) return null;
  return `https://www.nzpost.co.nz/tools/tracking?trackid=${encodeURIComponent(label)}`;
}

export function anPostTrackingUrl(value) {
  const label = normalizeTrackingNumber(value);
  if (!label) return null;
  return `https://www.anpost.com/Post-Parcels/Track/History?item=${encodeURIComponent(label)}`;
}

/**
 * Deep-link to the right national postal tracker.
 * @param {string} value tracking number
 * @param {{ country?: string, postalCode?: string, state?: string }} [hints]
 */
export function mailTrackingUrl(value, hints = {}) {
  if (!hasTrackingNumber(value)) return null;
  const country = inferMailCountry({
    country: hints.country,
    postalCode: hints.postalCode,
    state: hints.state,
    trackingNumber: value,
  });
  switch (country) {
    case 'CA':
      return canadaPostTrackingUrl(value);
    case 'GB':
      return royalMailTrackingUrl(value);
    case 'AU':
      return australiaPostTrackingUrl(value);
    case 'NZ':
      return nzPostTrackingUrl(value);
    case 'IE':
      return anPostTrackingUrl(value);
    default:
      return uspsTrackingUrl(value);
  }
}

export function mailCarrierLabel(value, hints = {}) {
  if (!hasTrackingNumber(value)) return 'Track package';
  const country = inferMailCountry({
    country: hints.country,
    postalCode: hints.postalCode,
    state: hints.state,
    trackingNumber: value,
  });
  switch (country) {
    case 'CA':
      return 'Track on Canada Post';
    case 'GB':
      return 'Track on Royal Mail';
    case 'AU':
      return 'Track on Australia Post';
    case 'NZ':
      return 'Track on NZ Post';
    case 'IE':
      return 'Track on An Post';
    default:
      return 'Track on USPS';
  }
}
